import { useEffect, useMemo, useState } from "react";
import cogoToast from "cogo-toast";
import { Button, Col, Form, Modal, Nav, Row, Table, ToggleButton } from "react-bootstrap";
import {
    useApproveCorporateApplicationMutation,
    useApproveCorporateDocumentMutation,
    useApproveCorporateW8beneMutation,
    useGetCorporateApplicationQuery,
    useRejectCorporateW8beneMutation,
    useUnlockCorporateW8beneMutation,
    useGetCorporateAdminDocumentsQuery,
    useManualVerifyCorporateCacMutation,
    useRejectDocumentMutation,
    useSubmitCorporateVerificationsMutation,
} from "../../../../../redux/apiSlice";
import MagnifiableImage from "../../../Common/components/MagnifiableImage";
import { canEditCompany, canEditReps, ProvisionErrorAlert } from "../corporateEditRules";
import { formatDate, formatStatus } from "../../../../util/Helpers";
import { defaultRejectReason } from "../../../PendingKyc/data/DefaultRejectReason";

// The three main corporate document types get their real names here; anything
// else just falls back to the raw type with underscores turned into spaces.
const DOCUMENT_TYPE_LABELS = {
    ENTITY_GOVERNING_DOCUMENT: "Memorandum & Articles of Association (MEMART)",
    CAC_REGISTRATION: "Certificate of Incorporation",
    BOARD_RESOLUTION: "Board Resolution",
};
const prettyEnum = (value) => {
    const key = `${value ?? ""}`.trim().toUpperCase();
    return DOCUMENT_TYPE_LABELS[key] || `${value ?? ""}`.replace(/_/g, " ").trim();
};
const normalizeStatus = (value) => `${value ?? ""}`.trim().toUpperCase().replace(/\s+/g, "_");
const REVIEWABLE_APPLICATION_STATUSES = new Set(["PENDING", "DRAFT"]);
// Company/rep BTA documents (CAC_REGISTRATION, ENTITY_GOVERNING_DOCUMENT,
// BOARD_RESOLUTION, CAC_STATUS_REPORT, GOVERNMENT_ID, etc.) all carry the
// ACCOUNT_TYPE_SUPPORT purpose. The opener's own personal KYC documents carry
// KYC_VERIFICATION instead, and don't belong to the company/rep package at all.
const KYC_VERIFICATION_PURPOSE = "KYC_VERIFICATION";

const asText = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "number") return `${value}`;
    if (typeof value === "string") return value.trim() || "-";
    if (Array.isArray(value)) return value.length ? value.map(asText).join(", ") : "-";
    return "-";
};

const normalizeList = (value) => {
    if (Array.isArray(value)) return value;

    const candidates = [
        value?.docs,
        value?.data,
        value?.items,
        value?.results,
        value?.rows,
        value?.data?.docs,
        value?.data?.data,
        value?.data?.items,
        value?.data?.results,
        value?.data?.rows,
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
    }

    return [];
};

const normalizeApplication = (value) => {
    if (!value) return null;

    const list = normalizeList(value);
    if (list.length > 0) return list[0];

    if (value?.data && typeof value.data === "object" && !Array.isArray(value.data)) {
        return value.data;
    }

    if (typeof value === "object" && !Array.isArray(value)) {
        return value;
    }

    return null;
};

const renderApplicationStatus = (value) => {
    const normalized = `${value ?? ""}`.trim().toUpperCase();

    if (!normalized) return "-";

    const color = {
        DRAFT: "secondary",
        IN_REVIEW: "warning",
        APPROVED: "success",
        REJECTED: "danger",
        ACCOUNT_CREATED: "primary",
    }[normalized] || "secondary";

    return (
        <span className={`label label-lg label-light-${color} label-inline`}>
            {normalized === "DRAFT" ? "Pending" : prettyEnum(normalized)}
        </span>
    );
};

const formatAddress = (value) => {
    if (!value || typeof value !== "object") return "-";

    return [
        ...(Array.isArray(value?.street_address) ? value.street_address : [value?.street1]).filter(Boolean),
        value?.unit,
        value?.city,
        value?.state,
        value?.country,
        value?.postal_code || value?.postalCode,
    ].filter(Boolean).join(", ") || "-";
};

const getDocumentId = (doc) => doc?.documentId || doc?.id || doc?.document_id;
const getDocumentUserId = (doc, application) => doc?.userId || application?.userId || application?.user?.userId || application?.application?.userId;
const getPartyDisplayName = (party) => (
    party?.fullName
    || [party?.givenName, party?.familyName].filter(Boolean).join(" ")
    || party?.email
    || party?.partyId
    || "Company Representative"
);
const getPartyRoleLabel = (value) => {
    const normalized = normalizeStatus(value);
    if (!normalized) return "Company Representative";
    if (normalized === "ADDITIONAL_PARTY") return "Company Representative";
    return prettyEnum(normalized);
};
const isPreviewUrl = (value) => /^https?:\/\//i.test(`${value ?? ""}`.trim());
const getDocumentImageSrc = (doc) => (
    doc?.previewUrl
    || (isPreviewUrl(doc?.document) ? doc?.document : null)
    || doc?.url
    || doc?.imageUrl
    || doc?.fileUrl
    // No fallback to "/users/document/{id}": that route lists a *user's* documents
    // by user id, so it never returned this file and showed a broken preview.
    || null
);
const getW8benePdfSrc = (application) => {
    const w8beneCertification = application?.w8beneCertification || application?.eligibility?.checks?.w8beneCertification || {};
    const pdfBase64 = w8beneCertification?.document?.pdfBase64;
    return pdfBase64 ? `data:application/pdf;base64,${pdfBase64}` : "";
};
const buildW8beneDocument = (application) => {
    const w8beneCertification = application?.w8beneCertification || application?.eligibility?.checks?.w8beneCertification || {};
    const pdfSrc = getW8benePdfSrc(application);

    if (!pdfSrc) return null;

    const companyName = (
        application?.legalEntity?.entityName
        || application?.application?.entityName
        || application?.entityName
        || "Organization"
    );

    return {
        id: "w8bene-document",
        documentId: "w8bene-document",
        type: "W8BEN_E",
        status: w8beneCertification?.status || "AVAILABLE",
        scope: "ORG",
        groupKind: "company",
        groupKey: "company",
        ownerLabel: companyName,
        previewUrl: pdfSrc,
        document: pdfSrc,
        mimeType: "application/pdf",
        purpose: "TAX_FORM",
        created: w8beneCertification?.document?.uploadedAt || w8beneCertification?.signature?.signedAt,
        updated: w8beneCertification?.audit?.updatedAt || w8beneCertification?.document?.uploadedAt,
        readOnly: true,
    };
};
const isPersonalKycVerificationDocument = (document) => {
    const purpose = normalizeStatus(document?.purpose);

    if (purpose) return purpose === KYC_VERIFICATION_PURPOSE;

    // Legacy fallback for documents that don't carry a purpose at all
    // (e.g. the collectDocuments() shim built from application.identityDocuments).
    return normalizeStatus(document?.scope) === "IDENTITY";
};

// A document belongs to this company's BTA package only when its applicationId
// matches the application being reviewed. Missing or mismatched applicationId
// means it belongs to some other application (or none) and should be ignored here.
const belongsToApplication = (document, applicationId) => {
    if (!applicationId) return true;
    const documentApplicationId = document?.applicationId;
    if (!documentApplicationId) return false;
    return `${documentApplicationId}` === `${applicationId}`;
};

const isPdfDocument = (document) => {
    const src = getDocumentImageSrc(document) || "";
    return (
        normalizeStatus(document?.mimeType) === "APPLICATION/PDF"
        || src.startsWith("data:application/pdf")
        || /\.pdf($|\?)/i.test(src)
        || normalizeStatus(document?.type) === "W8BEN_E"
    );
};

const collectDocuments = (application) => {
    const orgDocuments = (application?.orgDocuments || []).map((document) => ({
        ...document,
        scope: "ORG",
    }));

    const identityDocuments = (application?.identityDocuments || []).map((document) => ({
        ...document,
        scope: "IDENTITY",
    }));

    const partyDocuments = (application?.parties || []).flatMap((party) => (
        (party?.documents || []).map((document) => ({
            ...document,
            scope: "PARTY",
            ownerLabel: party?.fullName || party?.email || party?.partyId || "Company Representative",
            partyId: party?.partyId,
            refId: document?.refId || party?.partyId,
            role: party?.role,
        }))
    ));

    return [...orgDocuments, ...identityDocuments, ...partyDocuments];
};

const buildPartyLookup = (application) => {
    const parties = application?.parties || [];
    return parties.reduce((acc, party) => {
        const key = party?.partyId;
        if (key) {
            acc[key] = party;
        }
        return acc;
    }, {});
};

const buildPreviewUrlLookup = (application) => {
    const allDocuments = collectDocuments(application);

    return allDocuments.reduce((acc, document) => {
        const documentId = getDocumentId(document);
        if (documentId && document?.document && isPreviewUrl(document.document)) {
            acc[documentId] = document.document;
        }
        return acc;
    }, {});
};

const classifyCorporateDocument = ({ document, application, partiesById, previewUrlsByDocumentId }) => {
    const documentId = getDocumentId(document);
    const type = normalizeStatus(document?.type);
    const linkType = normalizeStatus(document?.linkedPerson?.linkType);
    // refId is the authoritative company-vs-representative signal: empty/null
    // means a company doc, set to a partyId means it's that rep's document.
    const partyId = document?.refId || document?.linkedPerson?.personId || document?.partyId || null;
    const linkedParty = partyId ? partiesById?.[partyId] : null;
    const companyName = (
        application?.legalEntity?.entityName
        || application?.application?.entityName
        || application?.entityName
        || document?.linkedPerson?.fullName
        || document?.ownerLabel
        || "Organization"
    );
    const isPartyDocument = !!partyId;

    let groupKind = "company";
    let groupKey = "company";
    let ownerLabel = companyName;

    if (isPartyDocument) {
        groupKind = "party";
        groupKey = partyId;
        ownerLabel = (
            document?.linkedPerson?.fullName
            || document?.ownerLabel
            || getPartyDisplayName(linkedParty)
        );
    }

    return {
        ...document,
        documentId,
        status: document?.status,
        previewUrl: previewUrlsByDocumentId?.[documentId] || null,
        groupKind,
        groupKey,
        ownerLabel,
        partyId: isPartyDocument ? partyId : null,
        refId: document?.refId ?? (isPartyDocument ? partyId : null),
        scope: document?.scope || (isPartyDocument ? "PARTY" : "ORG"),
        linkedParty,
        linkedPerson: document?.linkedPerson || null,
        linkType,
        type,
    };
};

const normalizeCorporateDocumentsForDisplay = (documents, application) => {
    const partiesById = buildPartyLookup(application);
    const previewUrlsByDocumentId = buildPreviewUrlLookup(application);

    return (documents || []).map((document) => classifyCorporateDocument({
        document,
        application,
        partiesById,
        previewUrlsByDocumentId,
    }));
};

const groupDocumentsForDisplay = (documents) => {
    const companyDocuments = documents.filter((document) => document?.groupKind !== "party");
    const partyGroupsMap = new Map();

    documents
        .filter((document) => document?.groupKind === "party")
        .forEach((document) => {
            const key = document?.groupKey || document?.partyId || "unassigned-party";
            const existing = partyGroupsMap.get(key);

            if (existing) {
                existing.documents.push(document);
                return;
            }

            partyGroupsMap.set(key, {
                key,
                label: document?.ownerLabel || "Company Representative",
                role: document?.linkedParty?.role || document?.linkType || "-",
                documents: [document],
            });
        });

    return {
        companyDocuments,
        partyGroups: Array.from(partyGroupsMap.values()),
    };
};

const DetailSection = ({ title, rows }) => (
    <div className="mb-7">
        <h6 className="font-weight-bolder text-dark mb-4">{title}</h6>
        <Table responsive bordered size="sm" className="mb-0">
            <tbody>
                {rows.map(({ label, value }) => (
                    <tr key={label}>
                        <th style={{ width: "35%", minWidth: "180px" }}>{label}</th>
                        <td>{value}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    </div>
);

const DocumentGroupCard = ({ title, subtitle, children }) => (
    <div
        className="mb-4 p-3 rounded"
        style={{
            border: "1px solid #E4E6EF",
            backgroundColor: "#FCFCFD",
        }}
    >
        <div className="d-flex align-items-start justify-content-between mb-3">
            <div>
                <div className="font-weight-bolder text-dark">
                    {title}
                </div>
                {subtitle && (
                    <div className="text-muted font-size-sm mt-1">
                        {subtitle}
                    </div>
                )}
            </div>
        </div>
        {children}
    </div>
);

const DocumentSectionTitle = ({ children }) => (
    <div className="mb-3 mt-5">
        <div
            className="font-weight-bolder text-dark"
            style={{
                fontSize: "1rem",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
            }}
        >
            {children}
        </div>
    </div>
);

const DocumentNavItem = ({ document, isActive, onClick }) => (
    <Nav.Link
        active={isActive}
        onClick={onClick}
        className="rounded mb-2 px-3 py-3 cursor-pointer"
        style={{
            cursor: "pointer",
            backgroundColor: isActive ? "#F3FFF7" : "#FFFFFF",
            color: "#3F4254",
            border: isActive ? "1px solid #C9EED6" : "1px solid #E4E6EF",
            boxShadow: isActive ? "0 10px 24px rgba(34, 164, 85, 0.10)" : "0 4px 12px rgba(15, 23, 42, 0.04)",
        }}
    >
        <div className="d-flex align-items-start justify-content-between">
            <div className="pr-3">
                <div className="font-weight-bolder text-dark mb-1">
                    {prettyEnum(document?.type || "Document")}
                </div>
                <div className="text-muted font-size-sm">
                    {asText(document?.ownerLabel)}
                </div>
            </div>
            <div className="text-right" style={{ minWidth: "92px" }}>
                {formatStatus(document?.status) || asText(document?.status)}
            </div>
        </div>
    </Nav.Link>
);

const SectionNav = ({ activeSection, onChange }) => {
    const sections = [
        { key: "overview", label: "Overview" },
        { key: "documents", label: "Documents" },
        { key: "pending-documents", label: "Pending Documents" },
        { key: "parties", label: "Company Representatives" },
        { key: "qoreid", label: "QoreID Details" },
        { key: "eligibility", label: "Eligibility" },
        { key: "verification", label: "Verification" },
        { key: "w8bene", label: "W-8BEN-E Review" },
    ];

    return (
        <Nav className="flex-column">
            {sections.map(({ key, label }) => (
                <Nav.Link
                    key={key}
                    active={activeSection === key}
                    onClick={() => onChange(key)}
                    className="rounded mb-2 cursor-pointer"
                    style={{
                        cursor: "pointer",
                        backgroundColor: activeSection === key ? "#F3FFF7" : "transparent",
                        color: activeSection === key ? "#22A455" : "#3F4254",
                        border: activeSection === key ? "1px solid #C9EED6" : "1px solid transparent",
                        fontWeight: 600,
                    }}
                >
                    {label}
                </Nav.Link>
            ))}
        </Nav>
    );
};

const OverviewSection = ({ application }) => {
    const reviewState = application?.eligibility || {};
    const rootApplication = application?.application || {};
    const user = application?.user || {};
    const legalEntity = application?.legalEntity || application?.kyc?.legalEntityData || {};

    return (
        <Row>
            <Col lg={12}>
                <ProvisionErrorAlert eligibility={reviewState} />
            </Col>
            <Col lg={6}>
                <DetailSection
                    title="Application"
                    rows={[
                        { label: "Application ID", value: asText(application?.applicationId || rootApplication?.applicationId) },
                        { label: "User ID", value: asText(application?.userId || user?.userId) },
                        { label: "User Group", value: asText(application?.userGroup || rootApplication?.userGroup) },
                        { label: "Account Type", value: asText(application?.accountType || rootApplication?.accountType) },
                        { label: "Account Name", value: asText(rootApplication?.accountName || application?.accountName) },
                        { label: "Entity Name", value: asText(rootApplication?.entityName || application?.entityName) },
                        { label: "Application Status", value: renderApplicationStatus(rootApplication?.status || application?.applicationStatus) },
                        { label: "Approved", value: asText(rootApplication?.approved ?? application?.approved) },
                        { label: "Company Details Editable", value: canEditCompany(application) ? "Yes" : "No" },
                        { label: "Representatives Editable", value: canEditReps(application) ? "Yes" : "No" },
                    ]}
                />

                <DetailSection
                    title="User"
                    rows={[
                        { label: "First Name", value: asText(user?.firstName) },
                        { label: "Last Name", value: asText(user?.lastName) },
                        { label: "Email", value: asText(user?.email) },
                        { label: "Phone", value: asText(user?.phone) },
                        { label: "Created", value: formatDate(rootApplication?.created || application?.created) },
                        { label: "Updated", value: formatDate(rootApplication?.updated || application?.updated) },
                    ]}
                />
            </Col>

            <Col lg={6}>
                <DetailSection
                    title="Legal Entity"
                    rows={[
                        { label: "Entity Name", value: asText(legalEntity?.entityName) },
                        { label: "Entity Type", value: asText(prettyEnum(legalEntity?.legalEntityTypeOther || legalEntity?.legalEntityType)) },
                        { label: "Registration Number", value: asText(legalEntity?.companyRegistrationNumber) },
                        { label: "TIN", value: asText(legalEntity?.taxIdentificationNumber) },
                        { label: "Country of Incorporation", value: asText(legalEntity?.countryOfIncorporation) },
                        { label: "Date of Incorporation", value: asText(legalEntity?.dateOfIncorporation) },
                        { label: "State of Incorporation", value: asText(legalEntity?.stateOfIncorporation) },
                        { label: "Industry", value: asText(prettyEnum(legalEntity?.industry)) },
                        { label: "Address", value: formatAddress(legalEntity?.legalAddress) },
                        { label: "Approved", value: asText(legalEntity?.approved) },
                    ]}
                />

                <DetailSection
                    title="Review Snapshot"
                    rows={[
                        { label: "Eligible", value: asText(reviewState?.eligible) },
                        { label: "Requirements Provided", value: asText(reviewState?.requirementsProvided) },
                        { label: "Can Create Account", value: asText(reviewState?.canCreateAccount) },
                        { label: "Can Proceed To Payment", value: asText(reviewState?.canProceedToPayment) },
                        { label: "Blocked Reason", value: asText(reviewState?.blockedReason) },
                        { label: "KYC Status", value: formatStatus(application?.kycStatus || reviewState?.checks?.kycStatus?.status) || asText(application?.kycStatus || reviewState?.checks?.kycStatus?.status) },
                        { label: "Document Review Status", value: formatStatus(application?.documentReviewStatus) || asText(application?.documentReviewStatus) },
                        { label: "Next Admin Action", value: asText(prettyEnum(application?.nextAdminAction)) },
                    ]}
                />
            </Col>
        </Row>
    );
};

const DocumentsSection = ({
    documents,
    activeDocumentKey,
    onSelectDocument,
    isFetchingDocuments,
    onApproveDocument,
    isApprovingDocument,
    onRejectDocument,
    isRejectingDocument,
    application,
    title = "Pending / Approved Documents",
    subtitle,
}) => {
    const w8beneDocument = useMemo(() => buildW8beneDocument(application), [application]);
    const displayDocuments = useMemo(
        () => (w8beneDocument ? [...documents, w8beneDocument] : documents),
        [documents, w8beneDocument]
    );
    const activeDocument = displayDocuments.find((document) => `${getDocumentId(document)}` === `${activeDocumentKey}`) || displayDocuments[0];
    const activeDocumentId = getDocumentId(activeDocument);
    const activeDocumentUserId = getDocumentUserId(activeDocument, application);
    const canApproveDocument = (
        normalizeStatus(activeDocument?.status) !== "APPROVED"
        && !!activeDocumentId
        && !!activeDocumentUserId
        && !activeDocument?.readOnly
    );
    const canRejectDocument = (
        normalizeStatus(activeDocument?.status) !== "REJECTED"
        && !!activeDocumentId
        && !!activeDocumentUserId
        && !activeDocument?.readOnly
    );
    const groupedDocuments = useMemo(
        () => groupDocumentsForDisplay(displayDocuments),
        [displayDocuments]
    );
    const displayPendingDocuments = useMemo(
        () => displayDocuments.filter((document) => normalizeStatus(document?.status) === "PENDING"),
        [displayDocuments]
    );
    const displayApprovedDocuments = useMemo(
        () => displayDocuments.filter((document) => normalizeStatus(document?.status) === "APPROVED"),
        [displayDocuments]
    );
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [rejectionReason, setRejectionReason] = useState("");

    useEffect(() => {
        setShowRejectForm(false);
        setRejectionReason("");
    }, [activeDocumentId]);

    if (isFetchingDocuments && displayDocuments.length === 0) {
        return (
            <div className="py-10 text-center text-muted">
                Loading documents...
            </div>
        );
    }

    if (displayDocuments.length === 0) {
        return (
            <div className="py-10 text-center text-muted">
                No documents found for this application.
            </div>
        );
    }

    return (
        <Row className="h-100">
            <Col lg={4} className="border-right pr-lg-5">
                <div
                    className="mb-4 p-4 rounded"
                    style={{
                        background: "linear-gradient(180deg, #F8FFF9 0%, #F4F8F6 100%)",
                        border: "1px solid #D9F1E2",
                    }}
                >
                    <div className="font-weight-bolder text-dark font-size-lg">
                        {title}
                    </div>
                    <div className="text-muted font-size-sm mt-1">
                        {subtitle || `${displayPendingDocuments.length} pending, ${displayApprovedDocuments.length} approved`}
                    </div>
                    <div className="d-flex flex-wrap mt-3">
                        <div
                            className="mr-2 mb-2 px-3 py-2 rounded"
                            style={{ backgroundColor: "#FFF4DE", minWidth: "96px" }}
                        >
                            <div className="text-dark font-weight-bolder">
                                {displayPendingDocuments.length}
                            </div>
                            <div className="text-muted font-size-xs text-uppercase">
                                Pending
                            </div>
                        </div>
                        <div
                            className="mb-2 px-3 py-2 rounded"
                            style={{ backgroundColor: "#E8FFF3", minWidth: "96px" }}
                        >
                            <div className="text-dark font-weight-bolder">
                                {displayApprovedDocuments.length}
                            </div>
                            <div className="text-muted font-size-xs text-uppercase">
                                Approved
                            </div>
                        </div>
                    </div>
                </div>

                <Nav className="flex-column">
                    {groupedDocuments.companyDocuments.length > 0 && (
                        <>
                            <DocumentSectionTitle>Company Documents</DocumentSectionTitle>
                            <DocumentGroupCard
                                title="All Company Documents"
                                subtitle={`${groupedDocuments.companyDocuments.length} item${groupedDocuments.companyDocuments.length === 1 ? "" : "s"}`}
                            >
                                {groupedDocuments.companyDocuments.map((document) => {
                                    const documentId = getDocumentId(document);
                                    const isActive = `${documentId}` === `${getDocumentId(activeDocument)}`;

                                    return (
                                        <DocumentNavItem
                                            key={documentId}
                                            document={document}
                                            isActive={isActive}
                                            onClick={() => onSelectDocument(documentId)}
                                        />
                                    );
                                })}
                            </DocumentGroupCard>
                        </>
                    )}

                    {groupedDocuments.partyGroups.length > 0 && (
                        <DocumentSectionTitle>Representative Documents</DocumentSectionTitle>
                    )}

                    {groupedDocuments.partyGroups.map((group) => (
                        <DocumentGroupCard
                            key={group.key}
                            title={group.label}
                            subtitle={`${getPartyRoleLabel(group.role)}${group.documents.length ? ` · ${group.documents.length} doc${group.documents.length === 1 ? "" : "s"}` : ""}`}
                        >
                            {group.documents.map((document) => {
                                const documentId = getDocumentId(document);
                                const isActive = `${documentId}` === `${getDocumentId(activeDocument)}`;

                                return (
                                    <DocumentNavItem
                                        key={documentId}
                                        document={document}
                                        isActive={isActive}
                                        onClick={() => onSelectDocument(documentId)}
                                    />
                                );
                            })}
                        </DocumentGroupCard>
                    ))}
                </Nav>
            </Col>

            <Col lg={8}>
                <div className="rounded-lg border mb-4" style={{ height: "360px" }}>
                    {!getDocumentImageSrc(activeDocument) ? (
                        <div className="h-100 d-flex align-items-center justify-content-center text-muted text-center px-4">
                            No preview available for this document.
                        </div>
                    ) : isPdfDocument(activeDocument) ? (
                        <iframe
                            title={prettyEnum(activeDocument?.type || "Document")}
                            src={getDocumentImageSrc(activeDocument)}
                            style={{ width: "100%", height: "100%", border: 0 }}
                        />
                    ) : (
                        <MagnifiableImage
                            height="100%"
                            width="100%"
                            src={getDocumentImageSrc(activeDocument)}
                            alt={prettyEnum(activeDocument?.type || "Document")}
                        />
                    )}
                </div>

                <DetailSection
                    title="Document Details"
                    rows={[
                        { label: "Document Type", value: asText(prettyEnum(activeDocument?.type)) },
                        { label: "Owner", value: asText(activeDocument?.ownerLabel) },
                        { label: "Link Type", value: asText(prettyEnum(activeDocument?.linkType)) },
                        { label: "Company Representative ID / Ref ID", value: asText(activeDocument?.partyId || activeDocument?.refId) },
                        { label: "Status", value: formatStatus(activeDocument?.status) || asText(activeDocument?.status) },
                        { label: "ID Type", value: asText(activeDocument?.idType) },
                        { label: "Side", value: asText(activeDocument?.side) },
                        { label: "Purpose", value: asText(prettyEnum(activeDocument?.purpose)) },
                        { label: "Mime Type", value: asText(activeDocument?.mimeType) },
                        // Only relevant once a document has actually been rejected -
                        // showing "-" for every approved/pending document was just noise.
                        ...(normalizeStatus(activeDocument?.status) === "REJECTED"
                            ? [{ label: "Rejection Reason", value: asText(activeDocument?.rejectionReason) }]
                            : []),
                        { label: "Created", value: formatDate(activeDocument?.created) },
                        { label: "Updated", value: formatDate(activeDocument?.updated) },
                        // Rows with nothing behind them (e.g. Mime Type, ID Type, Side on a
                        // non-ID document) are dropped instead of showing a bare "-".
                    ].filter((row) => asText(row.value) !== "-")}
                />

                {!showRejectForm ? (
                    <div className="d-flex">
                        <Button
                            variant="outline-danger"
                            className="mr-3"
                            disabled={!canRejectDocument || isRejectingDocument}
                            onClick={() => setShowRejectForm(true)}
                        >
                            Reject
                        </Button>
                        <Button
                            variant="success"
                            disabled={!canApproveDocument || isApprovingDocument}
                            onClick={() => onApproveDocument({
                                documentId: activeDocumentId,
                                userId: activeDocumentUserId,
                            })}
                            style={{
                                backgroundColor: "#22A455",
                                borderColor: "#22A455",
                            }}
                        >
                            {isApprovingDocument ? "Approving..." : "Approve"}
                        </Button>
                    </div>
                ) : (
                    <div className="border rounded p-4">
                        <Form.Group className="mb-4">
                            <Form.Label className="font-weight-bolder">Rejection Reason</Form.Label>
                            <Form.Control
                                type="text"
                                value={rejectionReason}
                                onChange={(event) => setRejectionReason(event.target.value)}
                                placeholder="Enter reason for rejection..."
                                disabled={isRejectingDocument}
                            />
                        </Form.Group>

                        <div className="pb-4">
                            {defaultRejectReason.map(({ id, text }) => (
                                <ToggleButton
                                    key={id}
                                    id={`corporate-rejection-reason-${id}`}
                                    type="radio"
                                    name="corporate-rejection-reason"
                                    value={text}
                                    variant={`outline-${rejectionReason === text ? "danger" : "secondary"}`}
                                    className="mr-2 mb-2"
                                    checked={rejectionReason === text}
                                    onChange={(event) => setRejectionReason(event.currentTarget.value)}
                                >
                                    {text}
                                </ToggleButton>
                            ))}
                        </div>

                        <div className="d-flex">
                            <Button
                                variant="outline-dark"
                                className="mr-3"
                                onClick={() => {
                                    setShowRejectForm(false);
                                    setRejectionReason("");
                                }}
                                disabled={isRejectingDocument}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="dark"
                                disabled={!rejectionReason.trim() || isRejectingDocument}
                                onClick={async () => {
                                    await onRejectDocument({
                                        ...activeDocument,
                                        documentId: activeDocumentId,
                                        userId: activeDocumentUserId,
                                        userGroup: activeDocument?.userGroup || application?.userGroup || application?.application?.userGroup,
                                        rejectionReason: rejectionReason.trim(),
                                        meta: activeDocument,
                                    });
                                    setShowRejectForm(false);
                                    setRejectionReason("");
                                }}
                            >
                                {isRejectingDocument ? "Rejecting..." : "Reject"}
                            </Button>
                        </div>
                    </div>
                )}
            </Col>
        </Row>
    );
};

const PartiesSection = ({ application }) => {
    const parties = application?.parties || [];

    if (parties.length === 0) {
        return (
            <div className="py-10 text-center text-muted">
                No company representatives found for this application.
            </div>
        );
    }

    return (
        <>
            <div className="table-responsive mb-7">
                <Table className="table table-head-custom table-vertical-center overflow-hidden">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Role</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Title</th>
                            <th>Docs</th>
                        </tr>
                    </thead>
                    <tbody>
                        {parties.map((party) => (
                            <tr key={party?.partyId}>
                                <td>{asText(party?.fullName || [party?.givenName, party?.familyName].filter(Boolean).join(" "))}</td>
                                <td>{asText(getPartyRoleLabel(party?.role))}</td>
                                <td>{asText(party?.email)}</td>
                                <td>{asText(party?.phone)}</td>
                                <td>{asText(party?.titleAtCompany)}</td>
                                <td>{asText((party?.documents || []).length)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {parties
                .filter((party) => party?.role !== "PRIMARY")
                .map((party) => (
                    <div key={party?.partyId} className="mb-7">
                        <h6 className="font-weight-bolder text-dark mb-4">
                            {asText(party?.fullName || [party?.givenName, party?.familyName].filter(Boolean).join(" "))}
                        </h6>
                        <Row>
                            <Col lg={6}>
                                <DetailSection
                                    title="Company Representative Details"
                                    rows={[
                                        { label: "Role", value: asText(getPartyRoleLabel(party?.role)) },
                                        { label: "Date of Birth", value: asText(party?.dateOfBirth) },
                                        { label: "Country of Birth", value: asText(party?.countryOfBirth) },
                                        { label: "Country of Citizenship", value: asText(party?.countryOfCitizenship) },
                                        { label: "Title At Company", value: asText(party?.titleAtCompany) },
                                        { label: "Funding Source", value: asText((party?.fundingSource || []).map(prettyEnum)) },
                                        { label: "Address", value: formatAddress(party?.residentialAddress) },
                                    ]}
                                />
                            </Col>
                            <Col lg={6}>
                                <DetailSection
                                    title="Company Representative Checks"
                                    rows={[
                                        { label: "Control Person", value: asText(party?.disclosures?.is_control_person) },
                                        { label: "Politically Exposed", value: asText(party?.disclosures?.is_politically_exposed) },
                                        { label: "Immediate Family Exposed", value: asText(party?.disclosures?.immediate_family_exposed) },
                                        { label: "Affiliated Exchange/FINRA", value: asText(party?.disclosures?.is_affiliated_exchange_or_finra) },
                                        { label: "ID Numbers", value: asText((party?.idNumbers || []).map((item) => `${item?.type}: ${item?.value}`)) },
                                    ]}
                                />
                            </Col>
                        </Row>
                    </div>
                ))}
        </>
    );
};

const EligibilitySection = ({ application }) => {
    const eligibility = application?.eligibility || {};
    const checks = eligibility?.checks || {};
    const kycSections = checks?.kycSections || {};
    const missing = eligibility?.missing || [];

    return (
        <>
            <ProvisionErrorAlert eligibility={eligibility} />
            <Row>
                <Col lg={6}>
                    <DetailSection
                        title="Eligibility Summary"
                        rows={[
                            { label: "User Type", value: asText(eligibility?.userType) },
                            { label: "Market", value: asText(eligibility?.market) },
                            { label: "Partner", value: asText(eligibility?.partner) },
                            { label: "Eligible", value: asText(eligibility?.eligible) },
                            { label: "Requirements Provided", value: asText(eligibility?.requirementsProvided) },
                            { label: "Can Create Account", value: asText(eligibility?.canCreateAccount) },
                            { label: "Can Proceed To Payment", value: asText(eligibility?.canProceedToPayment) },
                            { label: "Blocked Reason", value: asText(eligibility?.blockedReason) },
                        ]}
                    />
                </Col>

                <Col lg={6}>
                    <DetailSection
                        title="Checks Snapshot"
                        rows={[
                            { label: "KYC Level", value: asText(checks?.kycStatus?.level) },
                            { label: "KYC Status", value: asText(checks?.kycStatus?.status) },
                            { label: "Meets Min Level", value: asText(checks?.kycStatus?.meetsMinLevel) },
                            { label: "Legal Entity Present", value: asText(checks?.legalEntityData?.present) },
                            { label: "Legal Entity Approved", value: asText(checks?.legalEntityData?.approved) },
                            { label: "Kickstart Complete", value: asText(checks?.kickstart?.complete) },
                            { label: "W-8BEN-E Complete", value: asText(checks?.w8beneCertification?.complete) },
                            { label: "Tax Forms Complete", value: asText(checks?.taxForms?.complete) },
                        ]}
                    />
                </Col>
            </Row>

            <div className="mb-7">
                <h6 className="font-weight-bolder text-dark mb-4">Missing Requirements</h6>
                {missing.length === 0 ? (
                    <div className="text-muted">No missing items reported.</div>
                ) : (
                    <ul className="mb-0 pl-5">
                        {missing.map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="table-responsive">
                <Table className="table table-head-custom table-vertical-center overflow-hidden">
                    <thead>
                        <tr>
                            <th>KYC Section</th>
                            <th>Present</th>
                            <th>Approved</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(kycSections).map(([key, section]) => (
                            <tr key={key}>
                                <td>{prettyEnum(key)}</td>
                                <td>{asText(section?.present)}</td>
                                <td>{asText(section?.approved)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </>
    );
};

const QoreIdSection = ({ application }) => {
    const qoreidKyb = application?.qoreidKyb || {};
    const cacResponse = qoreidKyb?.cac || {};
    const rawCac = cacResponse?.cac || {};
    const mappedCac = qoreidKyb?.cacMapped || {};
    const mappedCompany = mappedCac?.company || {};
    const mappedTotals = mappedCac?.totals || {};
    const partyPrefills = mappedCac?.partyPrefills || [];
    const legalEntityPrefill = mappedCac?.legalEntityPrefill || {};
    const w8beneCertification = application?.w8beneCertification || application?.eligibility?.checks?.w8beneCertification || {};
    const w8Document = w8beneCertification?.document || {};

    return (
        <>
            <Row>
                <Col lg={6}>
                    <DetailSection
                        title="QoreID KYB Summary"
                        rows={[
                            { label: "Result", value: asText(qoreidKyb?.result) },
                            { label: "CAC Verified", value: asText(qoreidKyb?.cacVerified) },
                            { label: "Verified At", value: formatDate(qoreidKyb?.verifiedAt) },
                            { label: "Verified Entity Name", value: asText(qoreidKyb?.verifiedEntityName) },
                            { label: "Verified Registration Number", value: asText(qoreidKyb?.verifiedRegistrationNumber) },
                            { label: "CAC Check Verified", value: asText(qoreidKyb?.matches?.cacCheckVerified) },
                            { label: "CAC Company Active", value: asText(qoreidKyb?.matches?.cacCompanyActive) },
                            { label: "Reasons", value: asText(qoreidKyb?.reasons) },
                            { label: "Mismatches", value: asText(qoreidKyb?.mismatches) },
                            { label: "Response ID", value: asText(qoreidKyb?.cacResponseId) },
                        ]}
                    />
                </Col>

                <Col lg={6}>
                    <DetailSection
                        title="CAC Company"
                        rows={[
                            { label: "Company Name", value: asText(mappedCompany?.companyName || rawCac?.companyName) },
                            { label: "RC Number", value: asText(mappedCompany?.rcNumber || rawCac?.rcNumber) },
                            { label: "Company Type", value: asText(prettyEnum(mappedCompany?.companyType || cacResponse?.metadata?.companyType || rawCac?.companyType)) },
                            { label: "Classification", value: asText(mappedCompany?.classification || rawCac?.classification) },
                            { label: "Company Email", value: asText(mappedCompany?.companyEmail || rawCac?.companyEmail) },
                            { label: "Registration Date", value: asText(mappedCompany?.registrationDate || rawCac?.registrationDate) },
                            { label: "State", value: asText(mappedCompany?.state || rawCac?.state) },
                            { label: "LGA", value: asText(mappedCompany?.lga || rawCac?.lga) },
                            { label: "Head Office Address", value: asText(mappedCompany?.headOfficeAddress || rawCac?.headOfficeAddress) },
                            { label: "Branch Address", value: asText(mappedCompany?.branchAddress || rawCac?.branchAddress) },
                        ]}
                    />
                </Col>
            </Row>

            <Row>
                <Col lg={6}>
                    <DetailSection
                        title="Mapped Totals"
                        rows={[
                            { label: "Affiliate Rows", value: asText(mappedTotals?.affiliateRows) },
                            { label: "Unique Individuals", value: asText(mappedTotals?.uniqueIndividuals) },
                            { label: "Shareholders Mapped", value: asText(mappedTotals?.shareholdersMapped) },
                            { label: "Shareholders With Shares", value: asText(mappedTotals?.shareholdersWithShares) },
                            { label: "Min Ownership Percent", value: asText(mappedTotals?.minOwnershipPercent) },
                            { label: "Total Shares Allotted", value: asText(mappedTotals?.totalSharesAllotted) },
                        ]}
                    />
                </Col>

                <Col lg={6}>
                    <DetailSection
                        title="Legal Entity Prefill"
                        rows={[
                            { label: "Entity Name", value: asText(legalEntityPrefill?.entityName) },
                            { label: "Entity Email", value: asText(legalEntityPrefill?.entityEmail) },
                            { label: "Registration Number", value: asText(legalEntityPrefill?.companyRegistrationNumber) },
                            { label: "Entity Type", value: asText(prettyEnum(legalEntityPrefill?.legalEntityTypeOther || legalEntityPrefill?.legalEntityType)) },
                            { label: "Country of Incorporation", value: asText(legalEntityPrefill?.countryOfIncorporation) },
                            { label: "Country of Tax Residence", value: asText(legalEntityPrefill?.countryOfTaxResidence) },
                            { label: "State of Incorporation", value: asText(legalEntityPrefill?.stateOfIncorporation) },
                            { label: "Date of Incorporation", value: asText(legalEntityPrefill?.dateOfIncorporation) },
                            { label: "Legal Address", value: formatAddress(legalEntityPrefill?.legalAddress) },
                        ]}
                    />
                </Col>
            </Row>

            <div className="mb-7">
                <h6 className="font-weight-bolder text-dark mb-4">Company Representative Prefills</h6>
                {partyPrefills.length === 0 ? (
                    <div className="text-muted">No representative prefills found.</div>
                ) : (
                    <div className="table-responsive">
                        <Table className="table table-head-custom table-vertical-center overflow-hidden">
                            <thead>
                                <tr>
                                    <th>Full Name</th>
                                    <th>Roles</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Title At Company</th>
                                    <th>Ownership %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {partyPrefills.map((item) => (
                                    <tr key={item?.prefillKey || item?.fullName}>
                                        <td>{asText(item?.fullName)}</td>
                                        <td>{asText((item?.roles || []).map(prettyEnum))}</td>
                                        <td>{asText(item?.email)}</td>
                                        <td>{asText(item?.phone || item?.phoneNumber)}</td>
                                        <td>{asText(item?.titleAtCompany)}</td>
                                        <td>{asText(item?.ownershipPercentage || item?.percentageOwnership)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </div>

            <Row>
                <Col lg={12}>
                    <DetailSection
                        title="W-8BEN-E Summary"
                        rows={[
                            { label: "Status", value: asText(w8beneCertification?.status) },
                            { label: "Current Step", value: asText(w8beneCertification?.currentStep) },
                            { label: "Entity Name", value: asText(w8beneCertification?.answers?.entityName) },
                            { label: "Foreign TIN", value: asText(w8beneCertification?.answers?.foreignTin) },
                            { label: "Entity Structure", value: asText(prettyEnum(w8beneCertification?.answers?.entityStructure)) },
                            { label: "Income Source", value: asText(prettyEnum(w8beneCertification?.answers?.incomeSource)) },
                            { label: "Classification Label", value: asText(w8beneCertification?.derived?.classificationLabel) },
                            { label: "FATCA Classification", value: asText(prettyEnum(w8beneCertification?.derived?.fatcaClassification)) },
                            { label: "Signed By", value: asText(w8beneCertification?.signature?.fullName) },
                            { label: "Signed Capacity", value: asText(w8beneCertification?.signature?.capacity) },
                            { label: "Signed At", value: formatDate(w8beneCertification?.signature?.signedAt) },
                            { label: "Uploaded To Alpaca", value: asText(w8Document?.uploadedToAlpaca) },
                        ]}
                    />
                </Col>
            </Row>
        </>
    );
};

/* ------------------------------------------------------------------ *
 *  W-8BEN-E review
 *
 *  A W-8BEN-E in MANUAL_REVIEW is on hold for compliance. Core resolves the
 *  hold with POST .../w8bene/{unlock|approve|reject} and an optional notes body:
 *    unlock  - back to IN_PROGRESS so the customer can finish, sign and submit
 *              (signing is what produces the PDF). Usually the right action.
 *    approve - marks the certification APPROVED; no PDF is created or uploaded.
 *    reject  - rejects the certification.
 *  The old footer button only ever called approve, so held certifications with
 *  no form were being marked approved.
 * ------------------------------------------------------------------ */

const W8_ACTIONS = {
    unlock: {
        label: "Unlock for customer",
        verb: "Unlocking",
        done: "W-8BEN-E unlocked — the customer can now finish and sign it",
        variant: "success",
        confirm: "This clears the hold and sets the W-8BEN-E back to In progress. The customer then finishes the form, previews, signs and submits it, which creates the PDF.",
    },
    approve: {
        label: "Approve without PDF",
        verb: "Approving",
        done: "W-8BEN-E approved",
        variant: "outline-success",
        confirm: "This marks the W-8BEN-E as APPROVED. No PDF is created or uploaded to Alpaca. Only do this if compliance has cleared it and no signed form is needed.",
    },
    reject: {
        label: "Reject",
        verb: "Rejecting",
        done: "W-8BEN-E rejected",
        variant: "outline-danger",
        confirm: "This rejects the W-8BEN-E certification.",
    },
};

const MANUAL_REVIEW_REASON_LABELS = {
    UNSUPPORTED_ENTITY_STRUCTURE: "The company type picked isn't supported by the automated form",
};

const openBase64Pdf = (base64) => {
    try {
        const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
        cogoToast.error("Couldn't open the PDF");
    }
};

const W8beneReviewSection = ({ application, applicationId }) => {
    const certification = application?.w8beneCertification || {};
    const check = application?.eligibility?.checks?.w8beneCertification || {};
    const status = normalizeStatus(certification?.status || check?.status || application?.w8beneStatus);
    const manualReview = Boolean(check?.manualReview || certification?.manualReview || status === "MANUAL_REVIEW");
    const reasons = [
        ...(check?.manualReviewReasons || []),
        ...(certification?.derived?.manualReviewReasons || []),
    ].filter((reason, index, list) => reason && list.indexOf(reason) === index);
    const answers = certification?.answers || {};
    const signedPdf = certification?.document?.pdfBase64;
    const previewPdf = certification?.document?.previewPdfBase64;
    const canResolve = manualReview || status === "PENDING_REVIEW";

    const [notes, setNotes] = useState("");
    const [pendingAction, setPendingAction] = useState(null);
    const [unlockW8, { isLoading: isUnlocking }] = useUnlockCorporateW8beneMutation();
    const [approveW8, { isLoading: isApproving }] = useApproveCorporateW8beneMutation();
    const [rejectW8, { isLoading: isRejecting }] = useRejectCorporateW8beneMutation();
    const isBusy = isUnlocking || isApproving || isRejecting;

    const runAction = async (action) => {
        const run = { unlock: unlockW8, approve: approveW8, reject: rejectW8 }[action];
        const body = notes.trim() ? { notes: notes.trim() } : {};
        try {
            await run({ applicationId, body }).unwrap();
            cogoToast.success(W8_ACTIONS[action].done);
            setPendingAction(null);
            setNotes("");
        } catch (error) {
            cogoToast.error(error?.data?.error || error?.data?.message || `Couldn't ${action} the W-8BEN-E`);
        }
    };

    if (!status && !check?.required) {
        return <div className="py-10 text-center text-muted">This application has no W-8BEN-E certification yet.</div>;
    }

    return (
        <>
            <DetailSection
                title="W-8BEN-E Status"
                rows={[
                    { label: "Status", value: asText(prettyEnum(status)) },
                    { label: "Current Step", value: asText(prettyEnum(certification?.currentStep || check?.currentStep)) },
                    { label: "On Compliance Hold", value: asText(manualReview) },
                    { label: "Blocked Reason", value: asText(check?.blockedReason) },
                    { label: "Signed PDF", value: signedPdf ? "Yes" : "No — created when the customer signs" },
                    { label: "Uploaded To Alpaca", value: asText(check?.uploadedToAlpaca ?? certification?.document?.uploadedToAlpaca) },
                ]}
            />

            {reasons.length > 0 && (
                <div className="mb-7">
                    <h6 className="font-weight-bolder text-dark mb-3">Why it's on hold</h6>
                    {reasons.map((reason) => (
                        <div key={reason} className="mb-2">
                            <code>{reason}</code>
                            {MANUAL_REVIEW_REASON_LABELS[reason] && (
                                <span className="text-muted ml-2">{MANUAL_REVIEW_REASON_LABELS[reason]}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <DetailSection
                title="Customer's answers so far"
                rows={[
                    { label: "Entity Name", value: asText(answers?.entityName) },
                    { label: "Entity Structure", value: asText(prettyEnum(answers?.entityStructure)) },
                    { label: "Income Source", value: asText(prettyEnum(answers?.incomeSource)) },
                    { label: "Country Of Incorporation", value: asText(answers?.countryOfIncorporation) },
                    { label: "Foreign TIN", value: asText(answers?.foreignTin) },
                    { label: "Legal Address", value: formatAddress(answers?.legalAddress) },
                ].filter((row) => row.value !== "-")}
            />

            {(signedPdf || previewPdf) && (
                <div className="mb-7">
                    {signedPdf && (
                        <Button variant="outline-primary" className="mr-3" onClick={() => openBase64Pdf(signedPdf)}>
                            View signed PDF
                        </Button>
                    )}
                    {!signedPdf && previewPdf && (
                        <Button variant="outline-secondary" onClick={() => openBase64Pdf(previewPdf)}>
                            View draft PDF (not signed)
                        </Button>
                    )}
                </div>
            )}

            {canResolve ? (
                <div className="p-4 rounded" style={{ border: "1px solid #E4E6EF", backgroundColor: "#FCFCFD" }}>
                    <h6 className="font-weight-bolder text-dark mb-2">Resolve the hold</h6>
                    <div className="text-muted mb-4">
                        Usually <b>Unlock</b>: the customer finishes and signs the form, which creates the PDF.
                        <b> Approve</b> marks it approved without any PDF.
                    </div>
                    <Form.Group className="mb-4">
                        <Form.Label>Notes (optional)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="e.g. Cleared after review — sole proprietorship confirmed"
                        />
                    </Form.Group>

                    {pendingAction ? (
                        <div className="alert alert-light-warning mb-0">
                            <div className="mb-3">{W8_ACTIONS[pendingAction].confirm}</div>
                            <Button
                                variant={pendingAction === "reject" ? "danger" : "success"}
                                className="mr-2"
                                disabled={isBusy}
                                onClick={() => runAction(pendingAction)}
                            >
                                {isBusy ? `${W8_ACTIONS[pendingAction].verb}...` : `Yes, ${W8_ACTIONS[pendingAction].label.toLowerCase()}`}
                            </Button>
                            <Button variant="light" disabled={isBusy} onClick={() => setPendingAction(null)}>
                                Cancel
                            </Button>
                        </div>
                    ) : (
                        <div className="d-flex flex-wrap">
                            {["unlock", "approve", "reject"].map((action) => (
                                <Button
                                    key={action}
                                    variant={W8_ACTIONS[action].variant}
                                    className="mr-3 mb-2"
                                    disabled={isBusy}
                                    onClick={() => setPendingAction(action)}
                                >
                                    {W8_ACTIONS[action].label}
                                </Button>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-muted">
                    Nothing to resolve: the W-8BEN-E isn't on a compliance hold.
                </div>
            )}
        </>
    );
};

const VerificationSection = ({
    application,
}) => {
    const alpacaBta = application?.alpacaBta || {};
    const checks = application?.eligibility?.checks || {};

    return (
        <>
            <Row>
                <Col lg={12}>
                    <DetailSection
                        title="Alpaca / Tax"
                        rows={[
                            { label: "Agreements Accepted At", value: formatDate(alpacaBta?.agreements?.acceptedAt) },
                            { label: "Agreements Version", value: asText(alpacaBta?.agreements?.version) },
                            { label: "Provision Status", value: asText(alpacaBta?.provisionStatus || checks?.alpacaProvision?.provisionStatus) },
                            { label: "Account Created At", value: formatDate(alpacaBta?.accountCreatedAt) },
                            { label: "W-8BEN-E Status", value: asText(checks?.w8beneCertification?.status) },
                            { label: "W-8BEN-E Blocked Reason", value: asText(checks?.w8beneCertification?.blockedReason) },
                            { label: "Tax Forms Complete", value: asText(checks?.taxForms?.complete) },
                            { label: "Tax Forms Blocked Reason", value: asText(checks?.taxForms?.blockedReason) },
                            { label: "All Alpaca Verifications Clear", value: asText(checks?.alpacaVerifications?.allClear) },
                        ]}
                    />
                </Col>
            </Row>
        </>
    );
};

const CorporateApplicationModal = ({ show, applicationId, summary, onHide }) => {
    const [activeSection, setActiveSection] = useState("overview");
    const [activeDocumentKey, setActiveDocumentKey] = useState(null);
    const [activePendingDocumentKey, setActivePendingDocumentKey] = useState(null);

    const {
        currentData: applicationResponse,
        isFetching,
    } = useGetCorporateApplicationQuery(
        { applicationId },
        { skip: !show || !applicationId }
    );
    const [
        approveCorporateApplication,
        {
            isLoading: isApprovingApplication,
            isSuccess: approvedApplicationSuccessfully,
        },
    ] = useApproveCorporateApplicationMutation();
    const [
        approveCorporateDocument,
        {
            isLoading: isApprovingDocument,
            isSuccess: approvedDocumentSuccessfully,
        },
    ] = useApproveCorporateDocumentMutation();
    const [
        rejectCorporateDocument,
        {
            isLoading: isRejectingDocument,
            isSuccess: rejectedDocumentSuccessfully,
        },
    ] = useRejectDocumentMutation();
    const [
        submitCorporateVerifications,
        {
            isLoading: isSubmittingVerifications,
            isSuccess: submittedVerificationsSuccessfully,
        },
    ] = useSubmitCorporateVerificationsMutation();
    const [
        manualVerifyCorporateCac,
        {
            isLoading: isManualVerifyingCac,
            isSuccess: manualVerifiedCorporateCacSuccessfully,
        },
    ] = useManualVerifyCorporateCacMutation();
    const application = useMemo(
        () => normalizeApplication(applicationResponse) || summary || null,
        [applicationResponse, summary]
    );

    const effectiveApplicationId = application?.applicationId || summary?.applicationId || applicationId;
    const {
        currentData: adminDocumentsResponse,
        isFetching: isFetchingDocuments,
    } = useGetCorporateAdminDocumentsQuery(
        {
            query: {
                applicationId: effectiveApplicationId,
            },
        },
        {
            skip: !show || !effectiveApplicationId,
        }
    );
    const {
        currentData: pendingAdminDocumentsResponse,
        isFetching: isFetchingPendingDocuments,
    } = useGetCorporateAdminDocumentsQuery(
        {
            query: {
                applicationId: effectiveApplicationId,
                status: "PENDING",
            },
        },
        {
            skip: !show || !effectiveApplicationId || activeSection !== "pending-documents",
            refetchOnMountOrArgChange: true,
        }
    );
    const documents = useMemo(() => {
        const endpointDocuments = normalizeList(adminDocumentsResponse);

        // Documents fetched from the admin endpoint aren't guaranteed to be
        // scoped to this application server-side, so also filter client-side
        // on applicationId. The collectDocuments() fallback is already scoped
        // to this application inherently (it's built from the application
        // object itself), so that check doesn't apply there.
        if (endpointDocuments.length > 0) {
            return normalizeCorporateDocumentsForDisplay(endpointDocuments, application)
                .filter((document) => (
                    !isPersonalKycVerificationDocument(document)
                    && belongsToApplication(document, effectiveApplicationId)
                ));
        }

        return normalizeCorporateDocumentsForDisplay(collectDocuments(application), application)
            .filter((document) => !isPersonalKycVerificationDocument(document));
    }, [adminDocumentsResponse, application, effectiveApplicationId]);

    const pendingDocuments = useMemo(
        () => documents.filter((document) => normalizeStatus(document?.status) === "PENDING"),
        [documents]
    );

    const pendingDocumentsByEndpoint = useMemo(
        () => normalizeCorporateDocumentsForDisplay(normalizeList(pendingAdminDocumentsResponse), application)
            .filter((document) => (
                !isPersonalKycVerificationDocument(document)
                && belongsToApplication(document, effectiveApplicationId)
            )),
        [application, pendingAdminDocumentsResponse, effectiveApplicationId]
    );
    const approvedDocuments = useMemo(
        () => documents.filter((document) => normalizeStatus(document?.status) === "APPROVED"),
        [documents]
    );

    const visibleDocuments = useMemo(
        () => {
            // Previously this prioritized pending/approved over rejected across
            // the WHOLE application's flat document list: if anything anywhere
            // (any party, any document type) was pending or approved, every
            // rejected document application-wide was dropped from view - with
            // no other tab to see them in, they just disappeared. Scope the
            // priority to each document's own slot (same owner + same document
            // type) instead, so a rejected document is only superseded when
            // THAT slot has a newer pending/approved attempt - not because
            // something unrelated elsewhere in the application happens to be
            // approved.
            const slotGroups = new Map();
            documents.forEach((document) => {
                const slotKey = `${document?.groupKey ?? "unknown"}:${document?.type ?? "unknown"}`;
                if (!slotGroups.has(slotKey)) slotGroups.set(slotKey, []);
                slotGroups.get(slotKey).push(document);
            });

            const perSlotResult = [];
            slotGroups.forEach((slotDocuments) => {
                const prioritized = slotDocuments.filter((document) => {
                    const status = normalizeStatus(document?.status);
                    return status === "PENDING" || status === "APPROVED";
                });
                perSlotResult.push(...(prioritized.length > 0 ? prioritized : slotDocuments));
            });

            // Same pending-before-approved ordering as before; anything left
            // over (a slot with no pending/approved attempt, so its rejected
            // document survived the filter above) is appended last.
            return [
                ...perSlotResult.filter((document) => normalizeStatus(document?.status) === "PENDING"),
                ...perSlotResult.filter((document) => normalizeStatus(document?.status) === "APPROVED"),
                ...perSlotResult.filter((document) => {
                    const status = normalizeStatus(document?.status);
                    return status !== "PENDING" && status !== "APPROVED";
                }),
            ];
        },
        [documents]
    );
    const hasApprovedCacStatusReport = useMemo(
        () => documents.some((document) => (
            normalizeStatus(document?.type) === "CAC_STATUS_REPORT"
            && normalizeStatus(document?.status) === "APPROVED"
        )),
        [documents]
    );

    const currentApplicationStatus = normalizeStatus(
        application?.application?.status || application?.status || application?.applicationStatus
    );
    const companyRegistrationNumber = (
        application?.companyRegistrationNumber
        || application?.application?.companyRegistrationNumber
        || application?.legalEntity?.companyRegistrationNumber
        || ""
    );
    const entityNameForManualCac = (
        application?.entityName
        || application?.application?.entityName
        || application?.legalEntity?.entityName
        || application?.accountName
        || application?.application?.accountName
        || ""
    );
    const isCacVerified = Boolean(
        application?.application?.cacVerified
        || application?.cacVerified
        || application?.qoreidKyb?.cacVerified
        || application?.qoreidKyb?.matches?.cacCheckVerified
    );
    const nonPrimaryParties = (application?.parties || []).filter((party) => normalizeStatus(party?.role) !== "PRIMARY");
    // Bug fix: this used to filter the raw `documents` list (full history,
    // every attempt ever uploaded). A document that was rejected once and
    // later re-uploaded/approved left its old rejected record sitting in
    // that raw list forever, so `.every(status === APPROVED)` below kept
    // failing - "not all approved" - even with zero pending documents and
    // every *current* document approved. `visibleDocuments` already
    // resolves each document slot down to its latest pending/approved
    // attempt (see its own comment above) for the document viewer; reuse
    // that same resolved list here so the approval check agrees with what
    // the viewer actually shows.
    const companyDocuments = visibleDocuments.filter((document) => document?.groupKind !== "party");
    const partyDocuments = visibleDocuments.filter((document) => document?.groupKind === "party");
    const approvalChecks = useMemo(() => {
        const kickstart = application?.eligibility?.checks?.kickstart || {};
        const legalEntity = application?.legalEntity || application?.application || {};
        const qoreidKyb = application?.qoreidKyb || {};
        const agreementsAccepted = Boolean(
            application?.application?.agreementsAcceptedAt
            || application?.alpacaBta?.agreements?.acceptedAt
            || kickstart?.agreementsAccepted
        );
        const cacVerified = Boolean(
            application?.application?.cacVerified
            || qoreidKyb?.cacVerified
            || qoreidKyb?.matches?.cacCheckVerified
        );
        const companyProfileComplete = Boolean(
            legalEntity?.entityName
            && legalEntity?.companyRegistrationNumber
            && (legalEntity?.legalAddress || legalEntity?.address)
            && (legalEntity?.legalEntityType || legalEntity?.legalEntityTypeOther)
            && (legalEntity?.entityEmail || legalEntity?.entityPhone)
        );
        const partiesConfigured = nonPrimaryParties.length > 0 && (application?.legalEntity?.ubos || []).length > 0;
        const orgDocumentsApproved = companyDocuments.length > 0
            && companyDocuments.every((document) => normalizeStatus(document?.status) === "APPROVED");
        const partyDocumentsApproved = partyDocuments.length > 0
            && partyDocuments.every((document) => normalizeStatus(document?.status) === "APPROVED");
        const noPendingDocuments = pendingDocuments.length === 0;

        const reasons = [];

        if (!REVIEWABLE_APPLICATION_STATUSES.has(currentApplicationStatus)) {
            reasons.push("Application is not in a reviewable status.");
        }
        if (!cacVerified) {
            reasons.push("CAC verification is still incomplete.");
        }
        if (!agreementsAccepted) {
            reasons.push("Agreements have not been accepted.");
        }
        if (!companyProfileComplete) {
            reasons.push("Company profile details are incomplete.");
        }
        if (!partiesConfigured) {
            reasons.push("Parties or UBOs are incomplete.");
        }
        if (!orgDocumentsApproved) {
            reasons.push("Required company documents are not all approved.");
        }
        if (!partyDocumentsApproved) {
            reasons.push("Required party documents are not all approved.");
        }
        if (!noPendingDocuments) {
            reasons.push("There are still pending documents.");
        }

        return {
            agreementsAccepted,
            cacVerified,
            companyProfileComplete,
            partiesConfigured,
            orgDocumentsApproved,
            partyDocumentsApproved,
            noPendingDocuments,
            reasons,
            canApprove: reasons.length === 0,
        };
    }, [application, companyDocuments, currentApplicationStatus, nonPrimaryParties.length, partyDocuments, pendingDocuments.length]);
    const shouldShowApproveButton = REVIEWABLE_APPLICATION_STATUSES.has(currentApplicationStatus);
    const nextAdminAction = normalizeStatus(application?.nextAdminAction || application?.application?.nextAdminAction);
    const w8beneCertification = application?.eligibility?.checks?.w8beneCertification || {};
    const w8beneStatus = normalizeStatus(
        w8beneCertification?.status
        || application?.w8beneCertification?.status
        || application?.w8beneStatus
    );
    const alpacaProvision = application?.eligibility?.checks?.alpacaProvision || {};
    const alpacaVerifications = application?.eligibility?.checks?.alpacaVerifications || {};
    const legalEntityPartyCreated = Boolean(
        alpacaProvision?.legalEntityPartyCreated
        || application?.alpacaBta?.legalEntityPartyId
    );
    const partyProvisionIncomplete = (application?.parties || [])
        .filter((party) => normalizeStatus(party?.role) !== "PRIMARY")
        .some((party) => !party?.alpacaPartyId);
    const verificationsIncomplete = !Boolean(alpacaVerifications?.allClear);
    const shouldShowSubmitVerificationsButton = (
        currentApplicationStatus === "APPROVED"
        && (
            nextAdminAction === "SUBMIT_VERIFICATIONS"
            || !legalEntityPartyCreated
            || partyProvisionIncomplete
            || verificationsIncomplete
        )
    );
    const shouldShowApproveW8beneButton = (
        nextAdminAction === "APPROVE_W8BENE"
        || w8beneStatus === "PENDING_REVIEW"
        || Boolean(w8beneCertification?.manualReview)
    );
    const shouldShowManualCacButton = (
        hasApprovedCacStatusReport
        && !isCacVerified
        && currentApplicationStatus !== "ACCOUNT_CREATED"
    );
    const canManualVerifyCac = Boolean(companyRegistrationNumber);

    useEffect(() => {
        if (!show) {
            setActiveSection("overview");
            setActiveDocumentKey(null);
            return;
        }

        const firstDocumentId = visibleDocuments.length ? getDocumentId(visibleDocuments[0]) : null;
        setActiveDocumentKey(firstDocumentId);
        const firstPendingDocumentId = pendingDocumentsByEndpoint.length ? getDocumentId(pendingDocumentsByEndpoint[0]) : null;
        setActivePendingDocumentKey(firstPendingDocumentId);
    }, [pendingDocumentsByEndpoint, show, visibleDocuments]);

    useEffect(() => {
        if (approvedApplicationSuccessfully) {
            cogoToast.success("Corporate application approved");
            onHide();
        }
    }, [approvedApplicationSuccessfully, onHide]);

    useEffect(() => {
        if (approvedDocumentSuccessfully) {
            cogoToast.success("Document approved");
        }
    }, [approvedDocumentSuccessfully]);

    useEffect(() => {
        if (rejectedDocumentSuccessfully) {
            cogoToast.success("Document rejected");
        }
    }, [rejectedDocumentSuccessfully]);

    useEffect(() => {
        if (submittedVerificationsSuccessfully) {
            cogoToast.success("Broker verifications submitted");
        }
    }, [submittedVerificationsSuccessfully]);


    useEffect(() => {
        if (manualVerifiedCorporateCacSuccessfully) {
            cogoToast.success("Manual CAC verify submitted");
        }
    }, [manualVerifiedCorporateCacSuccessfully]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="xl"
            scrollable
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Corporate Application Details
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ minHeight: "70vh" }}>
                {!application && isFetching && (
                    <div className="py-10 text-center text-muted">
                        Loading application details...
                    </div>
                )}

                {!application && !isFetching && (
                    <div className="py-10 text-center text-muted">
                        No application details found.
                    </div>
                )}

                {application && (
                    <Row style={{ minHeight: "60vh" }}>
                        <Col lg={3} className="border-right mb-5 mb-lg-0">
                            <SectionNav activeSection={activeSection} onChange={setActiveSection} />
                        </Col>

                        <Col lg={9}>
                            {activeSection === "overview" && (
                                <OverviewSection application={application} />
                            )}

                            {activeSection === "documents" && (
                                <DocumentsSection
                                    documents={visibleDocuments}
                                    pendingDocuments={pendingDocuments}
                                    approvedDocuments={approvedDocuments}
                                    activeDocumentKey={activeDocumentKey}
                                    onSelectDocument={setActiveDocumentKey}
                                    isFetchingDocuments={isFetchingDocuments}
                                    onApproveDocument={approveCorporateDocument}
                                    isApprovingDocument={isApprovingDocument}
                                    onRejectDocument={rejectCorporateDocument}
                                    isRejectingDocument={isRejectingDocument}
                                    application={application}
                                />
                            )}

                            {activeSection === "pending-documents" && (
                                <DocumentsSection
                                    documents={pendingDocumentsByEndpoint}
                                    pendingDocuments={pendingDocumentsByEndpoint}
                                    approvedDocuments={[]}
                                    activeDocumentKey={activePendingDocumentKey}
                                    onSelectDocument={setActivePendingDocumentKey}
                                    isFetchingDocuments={isFetchingPendingDocuments}
                                    onApproveDocument={approveCorporateDocument}
                                    isApprovingDocument={isApprovingDocument}
                                    onRejectDocument={rejectCorporateDocument}
                                    isRejectingDocument={isRejectingDocument}
                                    application={application}
                                    title="Pending Documents"
                                    subtitle={`${pendingDocumentsByEndpoint.length} pending item${pendingDocumentsByEndpoint.length === 1 ? "" : "s"} from admin documents with status=PENDING`}
                                />
                            )}

                            {activeSection === "parties" && (
                                <PartiesSection application={application} />
                            )}

                            {activeSection === "eligibility" && (
                                <EligibilitySection application={application} />
                            )}

                            {activeSection === "qoreid" && (
                                <QoreIdSection application={application} />
                            )}

                            {activeSection === "verification" && (
                                <VerificationSection application={application} />
                            )}

                            {activeSection === "w8bene" && (
                                <W8beneReviewSection
                                    application={application}
                                    applicationId={application?.applicationId || applicationId}
                                />
                            )}
                        </Col>
                    </Row>
                )}
            </Modal.Body>
            <Modal.Footer className="d-block">
                <div className="d-flex justify-content-between align-items-start flex-wrap">
                <div className="d-flex align-items-start flex-wrap">
                    {shouldShowApproveButton && (
                        <Button
                            variant="success"
                            disabled={isApprovingApplication || !approvalChecks.canApprove}
                            onClick={() => approveCorporateApplication({
                                applicationId: application?.applicationId || applicationId,
                                body: {
                                    statusComment: "Approved from Oversight admin review",
                                },
                            })}
                            style={{
                                backgroundColor: "#22A455",
                                borderColor: "#22A455",
                            }}
                        >
                            {isApprovingApplication ? "Approving..." : "Approve Application"}
                        </Button>
                    )}
                    {shouldShowSubmitVerificationsButton && (
                        <Button
                            variant="success"
                            className={shouldShowApproveButton ? "ml-3" : ""}
                            disabled={isSubmittingVerifications}
                            onClick={() => submitCorporateVerifications({
                                applicationId: application?.applicationId || applicationId,
                            })}
                            style={{
                                backgroundColor: "#22A455",
                                borderColor: "#22A455",
                            }}
                        >
                            {isSubmittingVerifications ? "Submitting..." : "Submit Verifications"}
                        </Button>
                    )}
                    {shouldShowApproveW8beneButton && (
                        <Button
                            variant="success"
                            className={shouldShowApproveButton || shouldShowSubmitVerificationsButton ? "ml-3" : ""}
                            onClick={() => setActiveSection("w8bene")}
                            style={{
                                backgroundColor: "#22A455",
                                borderColor: "#22A455",
                            }}
                        >
                            Review W-8BEN-E
                        </Button>
                    )}
                    {shouldShowManualCacButton && (
                        <Button
                            variant="outline-primary"
                            className={shouldShowApproveButton || shouldShowSubmitVerificationsButton || shouldShowApproveW8beneButton ? "ml-3" : ""}
                            disabled={isManualVerifyingCac || !canManualVerifyCac}
                            onClick={() => {
                                const body = {
                                    companyRegistrationNumber,
                                };

                                if (entityNameForManualCac) {
                                    body.entityName = entityNameForManualCac;
                                }

                                manualVerifyCorporateCac({
                                    applicationId: application?.applicationId || applicationId,
                                    body,
                                });
                            }}
                        >
                            {isManualVerifyingCac ? "Submitting..." : "Manual CAC Verify"}
                        </Button>
                    )}
                </div>
                <Button variant="light" onClick={onHide}>
                    Close
                </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default CorporateApplicationModal;
